/* ─────────────────────────────────────────────
   Bayu Cart (items + buy-buttons)
   - Single set of document-level listeners
     dispatches to the right handler via the
     event target's data-* attributes.
   - Items: quantity changes are debounced 1s
     before hitting /cart/change.js. Delete
     fires immediately and swaps the trash icon
     with a spinner until done.
   - Buy buttons: note saves on blur, discount
     applies on click and validates against the
     fresh cart, checkout is a direct redirect.
   - On any cart mutation, every Bayu cart
     section on the page is re-rendered together
     via the Shopify Section Rendering API.
───────────────────────────────────────────── */

(function () {
  const DEBOUNCE_MS = 1000;
  const SECTION_SELECTOR = '[data-section-type="bayu-cart-items"], [data-section-type="bayu-cart-buy-buttons"]';
  let cartJson = null;
  fetch('/cart.js')
    .then((res) => res.json())
    .then((json) => {
      cartJson = json;
    });

  /* ── DOM helpers ─────────────────────────── */

  function getRow(el) {
    return el.closest('[data-key]');
  }

  function getSection(el) {
    return el ? el.closest(SECTION_SELECTOR) : null;
  }

  function getCurrentQty(row) {
    const valueEl = row.querySelector('.bayu-ci__qty-value');
    return parseInt(valueEl?.value, 10) || 0;
  }

  function setRowQty(row, quantity) {
    row.querySelectorAll('.bayu-ci__qty-value').forEach((el) => {
      el.value = quantity;
    });
  }

  function setRemoveLoading(btn, isLoading) {
    btn.classList.toggle('is-loading', isLoading);
    btn.disabled = isLoading;
  }

  function showDiscountError(section, message) {
    const el = section.querySelector('[data-cbb-discount-error]');
    if (el) el.textContent = message || '';
  }

  function setApplyLoading(section, isLoading) {
    const btn = section.querySelector('[data-cbb-discount-apply]');
    if (!btn) return;
    btn.classList.toggle('is-loading', isLoading);
    btn.disabled = isLoading;
  }

  /* ── Cart API + section render ───────────── */

  async function refreshAllSections() {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    const ids = sections.map((el) => el.dataset.sectionId).filter(Boolean);
    if (!ids.length) return;

    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`;
      const res = await fetch(url);
      if (!res.ok) return;

      const data = await res.json();

      sections.forEach((section) => {
        const html = data[section.dataset.sectionId];
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        const fresh = tmp.querySelector(`[data-section-id="${section.dataset.sectionId}"]`);
        if (fresh) section.innerHTML = fresh.innerHTML;
      });

      document.dispatchEvent(new CustomEvent('bayu:cart:updated'));
    } catch (err) {
      console.error('[bayu-cart] section refresh failed:', err);
    }
  }

  async function changeQuantity(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });

    if (!res.ok) {
      console.error('[bayu-cart] cart change failed:', res.status);
      throw new Error('Cart change failed');
    }

    await refreshAllSections();
  }

  async function updateCart(payload) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('[bayu-cart] cart update failed:', res.status);
      throw new Error('Cart update failed');
    }

    return res.json();
  }

  /* ── Items: qty + remove ─────────────────── */

  /* Per-row debounce timers, keyed on item line key. */
  const qtyTimers = new Map();

  function scheduleQtyUpdate(key, quantity) {
    if (qtyTimers.has(key)) {
      clearTimeout(qtyTimers.get(key));
      qtyTimers.delete(key);
    }
    qtyTimers.set(
      key,
      setTimeout(() => changeQuantity(key, quantity), DEBOUNCE_MS)
    );
  }

  function onQtyClick(btn) {
    const row = getRow(btn);
    if (!row) return;

    const action = btn.dataset.qtyAction;
    const current = getCurrentQty(row);
    const next = action === 'increment' ? current + 1 : Math.max(1, current - 1);

    if (next === current) return;

    setRowQty(row, next);
    scheduleQtyUpdate(row.dataset.key, next);
  }

  function onRemoveClick(btn) {
    const row = getRow(btn);
    if (!row) return;

    setRemoveLoading(btn, true);
    changeQuantity(row.dataset.key, 0).catch(() => {
      setRemoveLoading(btn, false);
    });
  }

  function onQtyInputBlur(input) {
    const row = getRow(input);
    if (!row) return;

    let next = parseInt(input.value, 10);
    if (isNaN(next) || next < 1) next = 1;

    setRowQty(row, next);

    const key = row.dataset.key;
    if (qtyTimers.has(key)) {
      clearTimeout(qtyTimers.get(key));
      qtyTimers.delete(key);
    }

    changeQuantity(key, next);
  }

  /* ── Buy-buttons: note + discount ────────── */

  async function saveNote(noteEl) {
    const section = getSection(noteEl);
    if (!section) return;

    const newNote = (noteEl.value || '').trim();
    const oldNote = cartJson ? (cartJson.note || '').trim() : '';

    if (newNote === oldNote) return;

    cartJson = await updateCart({ note: newNote });
    await refreshAllSections();
  }

  const getMatchedDiscountCode = (cartData, code) => {
    const codes = Array.isArray(cartData.discount_codes) ? cartData.discount_codes : [];
    const target = code.toLowerCase();
    const matched = codes.find((d) => (d.code || '').toLowerCase() === target);
    return matched;
  }

  async function applyDiscount(section) {
    const input = section.querySelector('[data-cbb-discount-input]');
    if (!input) return;

    const code = (input.value || '').trim();
    showDiscountError(section, '');

    if (!code) {
      showDiscountError(section, 'Please enter a discount code.');
      return;
    }

    if (getMatchedDiscountCode(cartJson, code)) {
      showDiscountError(section, 'This discount code is already applied.');
      input.value = '';
      return;
    }

    setApplyLoading(section, true);
    try {
      cartJson = await updateCart({ discount: code });
      const matched = getMatchedDiscountCode(cartJson, code);

      if (!matched || matched.applicable === false) {
        input.value = '';
        showDiscountError(section, 'The discount code is invalid or cannot be applied.');
      } else {
        input.value = '';
        await refreshAllSections();
      }

    } catch (err) {
      showDiscountError(section, 'Could not apply the discount. Please try again.');
    } finally {
      setApplyLoading(section, false);
    }
  }

  async function removeDiscount(btn) {
    btn.disabled = true;
    try {
      cartJson = await updateCart({ discount: '' });
      await refreshAllSections();
    } catch (err) {
      btn.disabled = false;
    }
  }

  /* ── Unified delegated handlers ──────────── */

  function onClick(event) {
    const target = event.target;

    const qtyBtn = target.closest('[data-qty-action]');
    if (qtyBtn && getSection(qtyBtn)) return onQtyClick(qtyBtn);

    const lineItemDeleteButton = target.closest('[data-remove-action]');
    if (lineItemDeleteButton && getSection(lineItemDeleteButton)) return onRemoveClick(lineItemDeleteButton);

    const applyBtn = target.closest('[data-cbb-discount-apply]');
    if (applyBtn) {
      const section = getSection(applyBtn);
      if (section) {
        event.preventDefault();
        return applyDiscount(section);
      }
    }

    const tagRemoveBtn = target.closest('[data-cbb-tag-remove]');
    if (tagRemoveBtn && getSection(tagRemoveBtn)) {
      event.preventDefault();
      return removeDiscount(tagRemoveBtn);
    }
  }

  function onInput(event) {
    const target = event.target;

    const qtyInput = target.closest('[data-qty-input]');
    if (qtyInput && getSection(qtyInput)) {
      const cleaned = qtyInput.value.replace(/[^0-9]/g, '');
      if (cleaned !== qtyInput.value) qtyInput.value = cleaned;
      return;
    }

    const discountInput = target.closest('[data-cbb-discount-input]');
    if (discountInput) {
      const section = getSection(discountInput);
      if (section) showDiscountError(section, '');
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Enter') return;
    const target = event.target;

    const qtyInput = target.closest('[data-qty-input]');
    if (qtyInput && getSection(qtyInput)) {
      event.preventDefault();
      qtyInput.blur();
      return;
    }

    const discountInput = target.closest('[data-cbb-discount-input]');
    const section = getSection(discountInput)
    if (discountInput && section) {
      event.preventDefault();
      applyDiscount(section);
    }
  }

  function onBlur(event) {
    const target = event.target;

    const qtyInput = target.closest('[data-qty-input]');
    if (qtyInput && getSection(qtyInput)) return onQtyInputBlur(qtyInput);

    const noteEl = target.closest('[data-cbb-note]');
    if (noteEl && getSection(noteEl)) return saveNote(noteEl);
  }

  /* ── Init ────────────────────────────────── */

  function init() {
    /* Both Bayu cart sections embed this <script src>, so the
       IIFE can run twice on the cart page. Attach the document
       listeners only on the first run so each event fires the
       handler exactly once. */
    if (window.bayuCartReady) return;
    window.bayuCartReady = true;

    document.addEventListener('click', onClick);
    document.addEventListener('input', onInput);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('blur', onBlur, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
